import { FC, type ReactNode, useCallback } from 'react';
import {
  DataObjectCloneEntityType,
  DataObjectDetailsLayout,
  DataObjectGroupType,
  dataObjectDescriptionMapping,
  DataObjectType,
  DataObjectView,
  type DataObjectPanelNavigationParams,
  Spinner,
  useFetchGroupDescriptionRef,
  useGetDataObjectVersionEditQuery,
  useRemoveDataObjectVersionEditQueries,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { useNumericParam } from '../../common/useEntityIdParam';
import { NotFound } from '../NotFound';
import {
  DataObjectBreadcrumbs,
  DataObjectBreadcrumbsData,
} from './DataObjectBreadcrumbs';
import { RouteObject } from '../../common/types';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { path } from '../../paths';
import { useOpenTestCalculationReport } from './useOpenTestCalculation';
import { useOpenTestValidationReport } from './useOpenTestValidation';

export const DataObjectDetails: FC<{
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  navigateToDetails: (params: { id?: number; segment?: string }) => void;
  navigateToEdit: (
    params: { id?: number } & DataObjectPanelNavigationParams,
  ) => void;
  navigateToClone: (params: {
    entityType: DataObjectCloneEntityType;
    segment?: string;
  }) => void;
  navigateToDirectory: () => void;
  actions?: ReactNode;
}> = (props) => {
  const versionId = useNumericParam('id');

  if (!versionId) {
    return <NotFound />;
  }

  return <DataObjectDetailsInner {...props} versionId={versionId} />;
};

export interface DataObjectDetailsInnerProps {
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  versionId: number;
  view: DataObjectView;
  navigateToDetails: (params: { id?: number; segment?: string }) => void;
  navigateToEdit: (
    params: { id?: number } & DataObjectPanelNavigationParams,
  ) => void;
  navigateToClone: (params: {
    entityType: DataObjectCloneEntityType;
    segment?: string;
  }) => void;
  navigateToDirectory: () => void;
  actions?: ReactNode;
}

const DataObjectDetailsInner: FC<DataObjectDetailsInnerProps> = (props) => {
  const {
    routes,
    versionId,
    view,
    navigateToDetails,
    navigateToEdit,
    navigateToClone,
    navigateToDirectory,
    actions,
  } = props;
  const { resetAlert, setSuccess } = useAlertContext();

  const { data: dataObjectVersion, isLoading } =
    useGetDataObjectVersionEditQuery(
      {
        id: versionId,
      },
      {
        refetchOnMount: 'always',
      },
    );
  const removeQueryData = useRemoveDataObjectVersionEditQueries();

  const handleSuccess = useCallback(() => {
    setSuccess();
    removeQueryData();
  }, [removeQueryData, setSuccess]);

  const relationsType =
    dataObjectVersion?.dataObjectType === DataObjectType.IN
      ? 'INPUT'
      : (dataObjectVersion?.outputTypeCode ?? 'OUTPUT');
  const dataObjectId = dataObjectVersion?.dataObjectId;

  const navigateToRelations = useNavigateTo(path.relations);
  const fetchGroupDescriptionRef = useFetchGroupDescriptionRef();

  const navigateToRelationsGraph = useCallback(async () => {
    const groupOrDescriptionId = dataObjectId
      ? await fetchGroupDescriptionRef(dataObjectId, {
          groupType:
            relationsType === 'INPUT'
              ? DataObjectGroupType.I
              : DataObjectGroupType.O,
          outputType:
            dataObjectDescriptionMapping[
              relationsType as keyof typeof dataObjectDescriptionMapping
            ]?.outputType,
        })
      : undefined;

    navigateToRelations({
      type: relationsType,
      groupOrDescriptionId,
      showOneStep: 'true',
    });
  }, [
    dataObjectId,
    fetchGroupDescriptionRef,
    navigateToRelations,
    relationsType,
  ]);

  const openTestCalculationReport = useOpenTestCalculationReport();
  const openTestValidationReport = useOpenTestValidationReport();

  const spinner = isLoading ? <Spinner /> : null;

  if (!dataObjectVersion) {
    return spinner;
  }

  return (
    <>
      {spinner}
      <DataObjectDetailsLayout
        dataObjectView={view}
        dataObjectVersion={dataObjectVersion}
        breadCrumb={
          <DataObjectBreadcrumbs data={{ dataObjectVersion }} routes={routes} />
        }
        navigateToDirectory={navigateToDirectory}
        navigateToDetails={navigateToDetails}
        navigateToEdit={navigateToEdit}
        navigateToRelationsGraph={navigateToRelationsGraph}
        onClickClone={navigateToClone}
        preSubmit={resetAlert}
        onSuccess={handleSuccess}
        openTestCalculate={openTestCalculationReport}
        openTestValidate={openTestValidationReport}
        actions={actions}
      />
    </>
  );
};
